/**
 * Common MUI TextField props to ensure consistent behavior and prevent label overlay issues
 * This utility provides standardized InputLabelProps for all TextField components
 */

/**
 * Standard InputLabelProps for TextField components
 * @param {string} color - The color of the label (default: '#4285f4')
 * @param {boolean} shrink - Whether to shrink the label (default: true)
 * @returns {Object} InputLabelProps object
 */
export const getStandardInputLabelProps = (color = '#4285f4', shrink = true) => ({
  style: { color },
  shrink
});

/**
 * InputLabelProps specifically for select fields
 * @param {string} color - The color of the label (default: '#4285f4')
 * @returns {Object} InputLabelProps object for select fields
 */
export const getSelectInputLabelProps = (color = '#4285f4') => ({
  style: { color },
  shrink: true // Select fields should always have shrunk labels
});

/**
 * InputLabelProps for fields that may or may not have values
 * @param {string} color - The color of the label (default: '#4285f4')
 * @param {*} value - The field value to determine if label should shrink
 * @returns {Object} InputLabelProps object
 */
export const getDynamicInputLabelProps = (color = '#4285f4', value) => ({
  style: { color },
  shrink: Boolean(value) || false
});

/**
 * Complete TextField props for consistent styling and behavior
 * @param {Object} options - Configuration options
 * @param {string} options.color - Label color (default: '#4285f4')
 * @param {boolean} options.shrink - Whether to shrink label (default: true)
 * @param {string} options.className - Additional CSS classes
 * @param {Object} options.additionalProps - Any additional props to merge
 * @returns {Object} Complete TextField props object
 */
export const getStandardTextFieldProps = ({
  color = '#4285f4',
  shrink = true,
  className = 'without-padding inviteUserInput',
  additionalProps = {}
} = {}) => ({
  variant: 'outlined',
  className,
  InputLabelProps: getStandardInputLabelProps(color, shrink),
  ...additionalProps
});
